
/**
 * Utility functions for consistent logging throughout the application
 */

/**
 * Module-specific log levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Log configuration by module
 */
const moduleLogLevels: Record<string, LogLevel> = {
  // Set default log level for specific modules
  'session-dialog': 'info',
  'variation': 'info',
  // Add more module configs as needed
};

/**
 * Check if logging is enabled for a module at a specific level
 */
export const isLoggingEnabled = (module: string, level: LogLevel): boolean => {
  // Always log errors in any environment
  if (level === 'error') return true;
  
  // In production, only log errors
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  
  // Get configured level for module or default to 'info'
  const configuredLevel = moduleLogLevels[module] || 'info';
  
  // Log level hierarchy: debug < info < warn < error
  const levelPriority = {
    'debug': 0,
    'info': 1,
    'warn': 2,
    'error': 3
  };
  
  // Log if the requested level has equal or higher priority than configured
  return levelPriority[level] >= levelPriority[configuredLevel];
}

/**
 * Logger for a specific module
 */
export const createLogger = (module: string) => {
  return {
    debug: (message: string, ...data: any[]) => {
      if (isLoggingEnabled(module, 'debug')) {
        console.debug(`[${module}] ${message}`, ...data);
      }
    },
    info: (message: string, ...data: any[]) => {
      if (isLoggingEnabled(module, 'info')) {
        console.info(`[${module}] ${message}`, ...data);
      }
    },
    warn: (message: string, ...data: any[]) => {
      if (isLoggingEnabled(module, 'warn')) {
        console.warn(`[${module}] ${message}`, ...data);
      }
    },
    error: (message: string, ...data: any[]) => {
      // Errors are always logged
      console.error(`[${module}] ${message}`, ...data);
    }
  };
};
